/**
 * Environment Configuration
 * Centralized access to environment variables with validation
 */

interface AppConfig {
  apiUrl: string;
  appName: string;
  appVersion: string;
  isDevelopment: boolean;
  isProduction: boolean;
}

const CRM_APP_VERSION = '1.0.0';

/**
 * Validate and return environment configuration
 */
function createConfig(): AppConfig {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL;
  const appName = import.meta.env.VITE_APP_NAME || 'CRM';
  const appVersion = import.meta.env.VITE_APP_VERSION || CRM_APP_VERSION;

  // Validate required environment variables
  if (!apiUrl) {
    throw new Error('VITE_API_URL is required but not defined');
  }

  // Validate API URL format
  try {
    new URL(apiUrl);
  } catch {
    throw new Error(`VITE_API_URL must be a valid URL, got: ${apiUrl}`);
  }

  return {
    apiUrl: apiUrl.replace(/\/$/, ''), // Remove trailing slash
    appName,
    appVersion,
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
  };
}

/**
 * Application configuration object
 * Exported as a singleton to ensure consistent access
 */
export const config = createConfig();

/**
 * Helper function to get the full API endpoint URL
 * @param path - API path (with or without leading slash)
 * @returns Full URL to the API endpoint
 */
export function getApiUrl(path = ''): string {
  if (!path) {
    return config.apiUrl;
  }
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${config.apiUrl}${cleanPath}`;
}
